import { SidebarMenu } from "components";
import { useRef, useState, useEffect } from "react";
import SellersOrders from "../components/SellersOrders";
import CustomersVisitControl from "../components/CustomersVisitControl";
import { formatDateToISODateString, formatISODateStringToDate, exportToExcel, formatWholePercentage } from "utils";
import axios from "axios";
import { useAppSelector } from "hooks";
import { CircularProgress, Box, Tabs, Tab, Button, Popover, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Checkbox, Divider, Typography, TextField, Stack, Grid, Paper, InputAdornment } from "@mui/material";
import { CalendarMonth } from "@mui/icons-material";
import ViewVisit from "../components/ViewVisit";
import ViewCostumer from "../components/ViewCostumer";

const FILTERS_STORAGE_KEY = "sellers_control_filters_v1";
const ACTIVE_TAB_STORAGE_KEY = "sellers_control_active_tab_v1";
const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

interface PersistedSellersControlFilters {
	startDate: string;
	endDate: string;
	selectedVendors: string[] | null;
	timestamp: number;
}

function DateRangeControl({
	systemMin,
	today,
	uiStart,
	setUiStart,
	uiEnd,
	setUiEnd,
	onApply
}: {
	systemMin: Date;
	today: Date;
	uiStart: Date;
	setUiStart: (d: Date) => void;
	uiEnd: Date;
	setUiEnd: (d: Date) => void;
	onApply: () => void;
}) {
	const MAX_RANGE_MONTHS = 6;

	const toMidnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);

	function clampToSystem(date: Date) {
		let x = toMidnight(date);
		const min = toMidnight(systemMin);
		const max = toMidnight(today);
		if (x < min) x = min;
		if (x > max) x = max;
		return x;
	}

	function addMonths(date: Date, months: number) {
		const d = new Date(date.getTime());
		const day = d.getDate();
		d.setMonth(d.getMonth() + months);
		// Handle month overflow (e.g., March 31 -> add 1 month -> April 30)
		if (d.getDate() < day) {
			d.setDate(0); // go to last day of previous month
		}
		return d;
	}

	function normalizeWithAnchor(start: Date, end: Date, anchor: "start" | "end") {
		let s = clampToSystem(start);
		let e = clampToSystem(end);

		if (anchor === "start") {
			// cap end to start + MAX_RANGE_MONTHS
			const maxEnd = addMonths(s, MAX_RANGE_MONTHS);
			const cappedMaxEnd = maxEnd > today ? today : maxEnd;
			if (e < s) e = cappedMaxEnd;
			if (e > cappedMaxEnd) e = cappedMaxEnd;
		} else {
			// ensure start is valid (>= systemMin) and end - start <= MAX_RANGE_MONTHS
			const minStart = addMonths(e, -MAX_RANGE_MONTHS);
			const cappedMinStart = minStart < systemMin ? systemMin : minStart;

			// Si start es mayor que end, ajustarlo
			if (s > e) {
				s = cappedMinStart;
			} else if (s < cappedMinStart) {
				// Solo ajustar si el rango excede los 6 meses
				s = cappedMinStart;
			}
			// Si start >= systemMin y está dentro del rango de 6 meses, mantenerlo
		}

		return [s, e] as const;
	}

	const handleStartInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
		const raw = ev.target.value;
		const picked = formatISODateStringToDate(raw, false);
		if (!picked) return;
		const [s, e] = normalizeWithAnchor(picked, uiEnd, "start");
		setUiStart(s);
		setUiEnd(e);
	};

	const handleEndInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
		const raw = ev.target.value;
		const picked = formatISODateStringToDate(raw, false);
		if (!picked) return;
		const [s, e] = normalizeWithAnchor(uiStart, picked, "end");
		setUiStart(s);
		setUiEnd(e);
	};

	// compute dynamic min/max for inputs to reflect 6-month constraint
	const computedMaxEnd = (() => {
		return today
	})();

	// const computedMinStart = (() => {
	// 	const minStart = addMonths(uiEnd, -MAX_RANGE_MONTHS);
	// 	return minStart < systemMin ? systemMin : minStart;
	// })();

	return (
		<Stack direction="row" spacing={3} alignItems="center">
			<TextField
				type="date"
				label="Fecha de inicio"
				variant="outlined"
				InputLabelProps={{ shrink: true }}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<CalendarMonth color="primary" />
						</InputAdornment>
					),
				}}
				inputProps={{
					min: formatDateToISODateString(systemMin),
					max: formatDateToISODateString(uiEnd),
				}}
				value={formatDateToISODateString(uiStart)}
				onChange={handleStartInput}
				size="medium"
				sx={{ minWidth: 220 }}
			/>

			<TextField
				type="date"
				label="Fecha de fin"
				variant="outlined"
				InputLabelProps={{ shrink: true }}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<CalendarMonth color="primary" />
						</InputAdornment>
					),
				}}
				inputProps={{
					min: formatDateToISODateString(systemMin),
					max: formatDateToISODateString(computedMaxEnd),
				}}
				value={formatDateToISODateString(uiEnd)}
				onChange={handleEndInput}
				size="medium"
				sx={{ minWidth: 220 }}
			/>

			<Button variant="contained" color="primary" onClick={onApply}>
				Actualizar
			</Button>
		</Stack>
	);
}

export const SellersControlPage = () => {
	const { token } = useAppSelector((state) => state.user);
	const today = new Date();

	// Calcular la fecha mínima del sistema (6 meses atrás)
	const systemMin = (() => {
		const d = new Date(today);
		d.setMonth(d.getMonth() - 6);
		return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
	})();

	// Inicializar el rango por defecto (último mes)
	const defaultStart = (() => {
		const d = new Date(today);
		// d.setMonth(d.getMonth() - 1);
		d.setDate(1)
		return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
	})();

	const clampToSystemRange = (date: Date) => {
		const normalized = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
		if (normalized < systemMin) return systemMin;
		if (normalized > today) return new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
		return normalized;
	};

	const persistedFilters = (() => {
		try {
			const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
			if (!raw) return null;
			const parsed = JSON.parse(raw) as Partial<PersistedSellersControlFilters>;
			
			// Verificar si los filtros han expirado (más de 6 horas)
			if (parsed.timestamp && (Date.now() - parsed.timestamp) > SIX_HOURS_MS) {
				return null; // Los filtros han expirado
			}
			
			return {
				startDate: typeof parsed.startDate === "string" ? parsed.startDate : null,
				endDate: typeof parsed.endDate === "string" ? parsed.endDate : null,
				selectedVendors: Array.isArray(parsed.selectedVendors)
					? parsed.selectedVendors.map((x) => String(x))
					: null,
			};
		} catch {
			return null;
		}
	})();

	const initialStartDate = (() => {
		if (!persistedFilters?.startDate) return defaultStart;
		const parsed = formatISODateStringToDate(persistedFilters.startDate, false);
		if (!parsed) return defaultStart;
		return clampToSystemRange(parsed);
	})();

	const initialEndDate = (() => {
		if (!persistedFilters?.endDate) return new Date();
		const parsed = formatISODateStringToDate(persistedFilters.endDate, false);
		if (!parsed) return new Date();
		return clampToSystemRange(parsed);
	})();

	const [startDateSearchFilter, setStartDateSearchFilter] = useState<Date>(
		initialStartDate <= initialEndDate ? initialStartDate : initialEndDate
	);
	const [finishDateSearchFilter, setFinishDateSearchFilter] = useState<Date>(initialEndDate);

	const contentRef = useRef<HTMLDivElement | null>(null);
	const [selectedVendors, setSelectedVendors] = useState<string[] | null>(persistedFilters?.selectedVendors ?? null);
	const [vendorList, setVendorList] = useState<Array<{ code: string; name: string, checked: boolean }>>([]);
	const [loader, setLoader] = useState<boolean>(true);
	const [dataReport, setDataReport] = useState<any>({});
	const [backupDataReport, setBackupDataReport] = useState<any>({});
	const initialActiveReportTab = (() => {
		try {
			const savedTab = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
			if (savedTab === "visits" || savedTab === "orders" || savedTab === "viewCustomer" || savedTab === "viewVisit") {
				return savedTab;
			}
		} catch {
			// ignore persistence errors
		}
		return "visits";
	})();
	const [activeReportTab, setActiveReportTab] = useState<"visits" | "orders" | "viewCustomer" | "viewVisit">(initialActiveReportTab);
	const [vendorAnchorEl, setVendorAnchorEl] = useState<HTMLElement | null>(null);

	const normalize = (value: unknown) => String(value ?? "").trim().toLowerCase();

	const isVisitPerformed = (item: any) => item?.showFlagDistance === false;

	const getVendorCode = (item: any) => {
		const codeCandidates = [
			item?.userId,
			item?.salesPersonCode,
			item?.vendorCode,
			item?.vendorId,
			item?.salesPersonId,
			item?.sellerCode,
			item?.slpCode,
		].filter(Boolean);
		return codeCandidates.length ? String(codeCandidates[0]) : "";
	};

	const getVendorName = (item: any) => {
		const nameCandidates = [
			item?.vendorName,
			item?.salesPersonName,
			item?.sellerName,
			item?.userName,
		].filter(Boolean);
		return nameCandidates.length ? String(nameCandidates[0]) : "";
	};

	const persistFilters = (next?: Partial<{ startDate: Date; endDate: Date; selectedVendors: string[] | null }>) => {
		const payload: PersistedSellersControlFilters = {
			startDate: formatDateToISODateString(next?.startDate || startDateSearchFilter),
			endDate: formatDateToISODateString(next?.endDate || finishDateSearchFilter),
			selectedVendors: next?.selectedVendors !== undefined ? next.selectedVendors : selectedVendors,
			timestamp: Date.now(), // Guardar el timestamp actual
		};

		try {
			localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(payload));
		} catch {
			// ignore persistence errors
		}
	};

	const filterReportByVendors = (
		report: any,
		selectedCodes: string[] | null,
		vendors: Array<{ code: string; name: string }>
	) => {
		if (selectedCodes === null) return report || {};

		const selectedVendorNames = vendors
			.filter((v) => selectedCodes.includes(String(v.code)))
			.map((v) => normalize(v.name));

		const matchesVendor = (item: any) => {
			const vendorCode = getVendorCode(item);
			const vendorName = normalize(getVendorName(item));
			return selectedCodes.includes(vendorCode) || selectedVendorNames.includes(vendorName);
		};

		return {
			...report,
			ordersResume: (Array.isArray(report?.ordersResume) ? report.ordersResume : []).filter((item: any) => matchesVendor(item)),
			details: (Array.isArray(report?.details) ? report.details : []).filter((item: any) => matchesVendor(item)),
			detailsClients: (Array.isArray(report?.detailsClients) ? report.detailsClients : []).filter((item: any) => matchesVendor(item)),
		};
	};

	useEffect(() => {
		if (token) {
			const controller = new AbortController();
			getDataInitial(controller.signal);
			return () => controller.abort();
		}
	}, []); // eslint-disable-line

	const getDataInitial = async (signal: AbortSignal) => {
		setLoader(true);
		const response = await axios.get(`${process.env.REACT_APP_SERVICE_URL}/documents/get/v2/compliance/range`, {
			params: {
				dayInit: formatDateToISODateString(startDateSearchFilter),
				dayEnd: formatDateToISODateString(finishDateSearchFilter),
				_t: Date.now(),
			},
			headers: {
				Authorization: `UserToken ${token || ""}`,
				"Cache-Control": "no-cache, no-store, must-revalidate",
				Pragma: "no-cache",
				Expires: "0",
				"x-apicache-force-fetch": "true",
			},
			signal,
		});

		const listUser = (response.data?.data?.ordersResume || []).map((item: any) => ({
			code: item.userId,
			name: item.vendorName,
			checked: true,
		}));

		const validSelectedVendors = selectedVendors === null
			? null
			: selectedVendors.filter((vendorCode) => listUser.some((v: any) => String(v.code) === String(vendorCode)));

		const normalizedSelectedVendors = validSelectedVendors !== null && validSelectedVendors.length === listUser.length
			? null
			: validSelectedVendors;

		const withAll = normalizedSelectedVendors === null
			? [{ code: "all", name: "Todos", checked: true }, ...listUser]
			: [
				{ code: "all", name: "Todos", checked: false },
				...listUser.map((v: any) => ({ ...v, checked: normalizedSelectedVendors.includes(String(v.code)) }))
			];

		const apiReport = response.data?.data || {};


		// Mapear userId a detailsClients basándose en vendorName para asegurar que el filtro funcione
		const baseReport = {
			...apiReport,
			detailsClients: (Array.isArray(apiReport?.detailsClients) ? apiReport.detailsClients : []).map((item: any) => {
				let userId = item?.userId || "";
				const matchedVendor = (Array.isArray(apiReport?.ordersResume) ? apiReport.ordersResume : []).find(
					(vendor: any) => normalize(vendor?.vendorName) === normalize(item?.vendorName)
				);
				if (!userId) {
					userId = matchedVendor?.userId || "";
				}

				return {
					...item,
					userId,
				};
			}),
		};

		const nextReport = filterReportByVendors(baseReport, normalizedSelectedVendors, listUser);

		setVendorList(withAll);
		setSelectedVendors(normalizedSelectedVendors);
		setDataReport(nextReport)
		setBackupDataReport(apiReport)
		persistFilters({ selectedVendors: normalizedSelectedVendors });
		setLoader(false);
	}

	const openVendorPopover = (ev: React.MouseEvent<HTMLElement>) => setVendorAnchorEl(ev.currentTarget);
	const closeVendorPopover = () => setVendorAnchorEl(null);
	const vendorPopoverOpen = Boolean(vendorAnchorEl);

	const toggleVendor = (code: string) => {
		const availableVendors = vendorList.filter((v) => v.code !== "all").map((v) => ({ code: v.code, name: v.name }));

		if (code === "all") {
			const allChecked = vendorList.every(v => v.checked);
			const updatedList = vendorList.map(v => ({ ...v, checked: !allChecked }));
			const nextSelectedVendors = allChecked ? [] : null;
			setVendorList(updatedList);
			setSelectedVendors(nextSelectedVendors);
			setDataReport(nextSelectedVendors === null ? backupDataReport : filterReportByVendors(backupDataReport, nextSelectedVendors, availableVendors));
			persistFilters({ selectedVendors: nextSelectedVendors });
			return;
		} else {
			const updatedList = vendorList.map(v => {
				if (v.code === code) return { ...v, checked: !v.checked };
				else if (v.code === "all") return { ...v, checked: false };
				return v;
			});
			setVendorList(updatedList);
			const selected = updatedList.filter(v => v.code !== "all" && v.checked).map(v => v.code);
			const nextSelectedVendors = selected.length === updatedList.length - 1 ? null : selected;
			const nextAvailableVendors = updatedList.filter((v) => v.code !== "all").map((v) => ({ code: v.code, name: v.name }));
			setSelectedVendors(nextSelectedVendors);
			setDataReport(nextSelectedVendors === null ? backupDataReport : filterReportByVendors(backupDataReport, nextSelectedVendors, nextAvailableVendors));
			persistFilters({ selectedVendors: nextSelectedVendors });
			return;
		}
	};

	const handleApplyFilters = () => {
		persistFilters({
			startDate: startDateSearchFilter,
			endDate: finishDateSearchFilter,
			selectedVendors,
		});
		const controller = new AbortController();
		getDataInitial(controller.signal);
	};

	const handleChangeReportTab = (_: unknown, nextTab: "visits" | "orders" | "viewCustomer" | "viewVisit") => {
		setActiveReportTab(nextTab);
		try {
			localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, nextTab);
		} catch {
			// ignore persistence errors
		}
	};

	const handleExportExcel = () => {
		const ordersResume = Array.isArray(dataReport?.ordersResume) ? dataReport.ordersResume : [];
		const details = Array.isArray(dataReport?.details) ? dataReport.details : [];
		const detailsClients = Array.isArray(dataReport?.detailsClients) ? dataReport.detailsClients : [];

		const ordersResumeById = new Map<string, any>(
			ordersResume
				.filter((item: any) => item?.userId !== undefined && item?.userId !== null)
				.map((item: any) => [String(item.userId), item])
		);

		const payloads = [
			{
				data: ordersResume.map((item: any) => ({
					"Codigo Vendedor": item?.userId ?? "",
					"Vendedor": item?.vendorName ?? "",
					"Ruta": item?.router ?? "",
					"Clientes Visitados": Number(item?.visitClient) || 0,
					"Promedio Visitas por Dia": item?.averageOrdersPerDay ?? 0,
					"Clientes Asignados": Number(item?.allsClients) || 0,
					"Clientes Pendientes": (Number(item?.allsClients) || 0) - (Number(item?.visitClient) || 0),
					"Efectividad de Visita (%)": Number(item?.allsClients) > 0 ? formatWholePercentage(((Number(item?.visitClient) || 0) / Number(item?.allsClients)) * 100) : 0,
				})),
				sheetName: "Vista general"
			}, {
				data: details.map((item: any) => {
					const user = ordersResumeById.get(String(item?.userId ?? ""));
					return {
						"Codigo Vendedor": item?.userId ?? "",
						"Vendedor": item?.vendorName ?? user?.vendorName ?? "",
						"Ruta": item?.userRouter ?? user?.router ?? "",
						"Fecha de la Visita": item?.created_at ? new Date(item.created_at).toLocaleDateString() : "",
						"Hora de la Visita": item?.created_at ? new Date(item.created_at).toLocaleTimeString() : "",
						"Codigo Cliente": item?.cardCode ?? "",
						"Razon Social": item?.cardName ?? "",
						"Nombre Comercial": item?.cardFName ?? "",
						"Proximidad": item?.showFlagDistance ? "No" : "Sí",
						"Venta Efectiva": (() => {
						if (item?.status === "pending" || item?.status === "saved") {
							return item?.showFlagDistance ? "No (lejana)" : "Sí";
						}
						return "No";
						})(),
					};
				}),
				sheetName: "Vista visita"
			}, {
				data: detailsClients.map((item: any) => {
					return {
						"Codigo Cliente": item?.cardCode ?? "",
						"Razon Social": item?.cardName ?? "",
						"Nombre Comercial": item?.cardFName ?? "",
						"Vendedor": item?.vendorName ?? "",
						"Ruta": item?.userRouter ?? "",
						"Visita Realizada": isVisitPerformed(item) ? "Sí" : "No"

					};
				}),
				sheetName: "Vista clientes"
			}, {
				data: [{
					"Clientes visitados": ordersResume.reduce((acc: number, curr: any) => acc + (Number(curr?.visitClient) || 0), 0),
					"Clientes pendientes": ordersResume.reduce((acc: number, curr: any) => acc + ((Number(curr?.allsClients) || 0) - (Number(curr?.visitClient) || 0)), 0),
				}],
				sheetName: "Efectividad vendedores"
			}
		]
		exportToExcel("reporte_vendedores_" + new Date().toISOString(), payloads);
	};

	return (
		<Box ref={contentRef}>
			<Grid container sx={{ height: '100vh' }}>
				<Grid size={2} sx={{ borderRight: 2, borderColor: 'divider' }}>
					<SidebarMenu pageName="sellers_control" />
				</Grid>

				<Grid size={10} sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
					<Paper sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 2, borderColor: 'divider', px: 2, py: 2, borderRadius: 0 }} elevation={0}>
						<Box>
							<DateRangeControl
								systemMin={systemMin}
								today={today}
								uiStart={startDateSearchFilter}
								setUiStart={setStartDateSearchFilter}
								uiEnd={finishDateSearchFilter}
								setUiEnd={setFinishDateSearchFilter}
								onApply={handleApplyFilters}
							/>
						</Box>

						<Stack direction="row" spacing={2} alignItems="center">
							<Box>
								<Button variant="outlined" onClick={openVendorPopover}>
									Vendedores {selectedVendors === null ? "(Todos)" : `(${selectedVendors.length})`}
								</Button>
								<Popover
									open={vendorPopoverOpen}
									anchorEl={vendorAnchorEl}
									onClose={closeVendorPopover}
									anchorOrigin={{
										vertical: 'bottom',
										horizontal: 'left',
									}}
								>
									<Box sx={{ width: 390, maxHeight: 360, overflow: 'auto' }}>
										<List dense>
											<ListItem key="label">
												<ListItemText>
													<Typography variant="subtitle1">Filtrar por vendedor</Typography>
												</ListItemText>
											</ListItem>
											<Divider />
											{vendorList.map((v) => (
												<ListItemButton key={v.code} onClick={() => toggleVendor(v.code)}>
													<ListItemIcon>
														<Checkbox
															edge="start"
															checked={v.checked}
															tabIndex={-1}
															disableRipple
															inputProps={{ 'aria-labelledby': `checkbox-list-label-${v.code}` }}
														/>
													</ListItemIcon>
													<ListItemText id={`checkbox-list-label-${v.code}`} primary={v.name} />
												</ListItemButton>
											))}
											<Divider />
										</List>
									</Box>
								</Popover>
							</Box>
							<Button variant="contained" color="success" onClick={handleExportExcel}>
								Descargar Excel
							</Button>

						</Stack>
					</Paper>

					<Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "white" }}>
						<Tabs
							value={activeReportTab}
							onChange={handleChangeReportTab}
							indicatorColor="primary"
							textColor="primary"
						>
							<Tab value="visits" label="Efectividad de visitas" />
							<Tab value="orders" label="Detalle de visitas" />
							<Tab value="viewVisit" label="Vista visita" />
							<Tab value="viewCustomer" label="Vista clientes" />
						</Tabs>
					</Box>

					<Box sx={{ flex: 1, overflow: 'auto' }}>
						{loader ? (
							<Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
								<CircularProgress />
							</Box>
						) :
							<>
								{activeReportTab === "visits" && <CustomersVisitControl dataReport={dataReport.ordersResume || []} details={dataReport.configurationsDays} />}
								{activeReportTab === "orders" && <SellersOrders dataReport={dataReport.details || []} orderResume={dataReport.ordersResume || []} sellerSelected={selectedVendors} />}
								{activeReportTab === "viewVisit" && <ViewVisit data={dataReport.details || []} />}
								{activeReportTab === "viewCustomer" && <ViewCostumer data={dataReport.detailsClients || []} />}
							</>
						}
					</Box>
				</Grid>
			</Grid>
		</Box>
	);
};
